/**
 * Nowruz gift tags — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_nowruz`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const nowruzGiftTagsPack: ToilePack = {
    slug: 'nowruz-gift-tags',
    category: 'season',
    name: 'Nowruz gift tags',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'tags', label: 'To and from', subject: 'a 2x3 grid of gift tags, each with a punch hole and a goldfish, hyacinth or sabzeh motif, lettered "Nowruz Mobarak" with empty lines after "To:" and "From:"'},
        {key: 'eidi', label: 'Eidi envelope', subject: 'a fold-up envelope template for eidi money, dashed cut and fold lines, a blossom motif on the flap, and the word "Eidi" on the front'},
        {key: 'round', label: 'Round tags', subject: 'a grid of nine round tags with scalloped edges and a tile-star centre, each lettered "Happy Nowruz" round the rim with a blank space in the middle'},
    ],
    styles: [
        {key: 'tile', label: 'Persian tile', style: 'Persian glazed-tile ornament, fine arabesque borders and star motifs, crisp vector linework', palette: 'cobalt, turquoise and ivory with gold accents', framing: 'Tags in an even grid with a clean margin round the sheet'},
        {key: 'watercolor', label: 'Watercolor', style: 'loose watercolor illustration with soft bleeding edges and hand-painted motifs', palette: 'blossom pink, spring green and soft blue washes on textured white paper', framing: 'Tags spaced evenly on the paper with a clean margin'},
        {key: 'kraft', label: 'Kraft paper', style: 'simple stamped-print style on brown kraft paper, bold flat motifs, rough ink texture', palette: 'terracotta red and cream ink on kraft brown', framing: 'Tags laid out in a tidy grid on the kraft sheet with a margin'},
    ],
    directions: ['Gold foil', 'Pink', 'Black and white'],
    directionHint: 'try: Gold foil',
    seo: {
        title: 'Nowruz Gift Tags: Printable Tags and Eidi Envelope',
        metaDescription: 'Printable Nowruz gift tags and an eidi envelope: print-and-cut sheets with exact greetings and blank name lines, in three finishes.',
        h1: 'Nowruz gift tags: to-and-from tags, eidi envelope, round tags',
        tagline: 'Nine print-and-cut sheets, three tag kinds, three finishes each.',
        intro: 'Printable tags to tie on Nowruz gifts and on eidi, the new banknotes elders give children for the new year. The three kinds are a grid of to-and-from tags with goldfish, hyacinth or sabzeh motifs, a fold-up eidi envelope, and nine round tags lettered Happy Nowruz round the rim. Name lines are left empty for you to write in. Print, cut along the dashed lines and fold. Type a different greeting and the layout carries it.',
        useCases: [
            'tags on a Nowruz gift',
            'an eidi envelope for children',
            'a school or office gift table',
            'round tags for a party favour',
        ],
        faq: [
            {q: 'How do I make the eidi envelope?', a: 'Print the template, cut along the dashed lines and fold along the marked lines. The word Eidi is on the front.'},
            {q: 'Are the names already on the tags?', a: 'No. The name lines are blank, so you write the names by hand.'},
            {q: 'What is the difference between the styles?', a: 'Persian tile is cobalt and gold ornament, watercolor has soft pink and green washes, and kraft is a stamped terracotta print on brown paper.'},
        ],
    },
}
